"use client";

import { useForm } from "react-hook-form";
import type { Resolver, FieldErrors } from "react-hook-form";
import * as z from "zod";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useReCaptcha } from "next-recaptcha-v3";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import FileUpload from "@/components/ui/FileUpload";
import { sendCollaborationEmail } from "@/actions/email";
import { useState } from "react";

const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

type FormData = {
	companyName: string;
	email: string;
	projectType: string;
	projectStartDate: string;
	projectTeam: string;
	projectBudget: string;
	projectDetails?: string;
};

function CollaborationFormBase({
	getToken,
}: {
	getToken: (() => Promise<string>) | null;
}) {
	const t = useTranslations("CollaborationForm");

	const schema = useMemo(
		() =>
			z.object({
				companyName: z.string().min(2, t("companyNameRequired")),
				email: z.string().email(t("invalidEmail")),
				projectType: z.string().min(1, t("projectTypeRequired")),
				projectStartDate: z.string().min(1, t("startDateRequired")),
				projectTeam: z.string().min(1, t("teamRequired")),
				projectBudget: z.string().min(1, t("budgetRequired")),
				projectDetails: z.string().optional(),
			}),
		[t],
	);

	const customZodResolver: Resolver<FormData> = useMemo(
		() => (values) => {
			const result = schema.safeParse(values);
			if (result.success) {
				return { values: result.data, errors: {} };
			}
			const errors: FieldErrors<FormData> = {};
			for (const issue of result.error.issues) {
				const path = issue.path[0] as keyof FormData;
				if (path && !errors[path]) {
					errors[path] = { type: "validation", message: issue.message };
				}
			}
			return { values: {} as Record<string, never>, errors };
		},
		[schema],
	);

	const [status, setStatus] = useState<
		"idle" | "submitting" | "success" | "error"
	>("idle");
	const [files, setFiles] = useState<File[]>([]);
	const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

	const {
		register,
		handleSubmit,
		watch,
		setValue,
		formState: { errors, isValid },
		reset,
	} = useForm<FormData>({
		resolver: customZodResolver,
		mode: "onChange",
		defaultValues: {
			projectType: "Project-Based",
			projectStartDate: "Now",
			projectTeam: "Both",
		},
	});

	const onSubmit = async (data: FormData) => {
		if (files.length === 0) {
			alert(t("fileAlert"));
			return;
		}
		setStatus("submitting");
		setRecaptchaError(null);
		let token: string | null = null;
		if (getToken) {
			try {
				token = await getToken();
			} catch {
				setRecaptchaError(t("recaptchaError"));
				setStatus("idle");
				return;
			}
		}
		const formData = new FormData();
		formData.append("companyName", data.companyName);
		formData.append("email", data.email);
		formData.append("projectType", data.projectType);
		formData.append("projectStartDate", data.projectStartDate);
		formData.append("projectTeam", data.projectTeam);
		formData.append("projectBudget", data.projectBudget);
		if (data.projectDetails)
			formData.append("projectDetails", data.projectDetails);
		if (token) formData.append("recaptchaToken", token);

		files.forEach((file) => {
			formData.append("attachments", file);
		});

		const result = await sendCollaborationEmail(formData);
		if (result.success) {
			setStatus("success");
			reset();
			setFiles([]);
		} else {
			setStatus("error");
		}
	};

	const setSingleValue = (field: keyof FormData, value: string) => {
		setValue(field, value);
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="space-y-6 bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md"
		>
			<div>
				<Input
					{...register("companyName")}
					placeholder={t("companyNamePlaceholder")}
					className={errors.companyName ? "border-red-500" : ""}
				/>
				{errors.companyName && (
					<p className="text-red-500 text-sm mt-1">
						{errors.companyName.message}
					</p>
				)}
			</div>

			<div>
				<Input
					{...register("email")}
					placeholder={t("emailPlaceholder")}
					type="email"
					className={errors.email ? "border-red-500" : ""}
				/>
				{errors.email && (
					<p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					{t("projectTypeLabel")}
				</label>
				<div className="flex flex-wrap gap-2">
					{["Project-Based", "Long-Term"].map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => setSingleValue("projectType", type)}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
								watch("projectType") === type
									? "bg-primary text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
							}`}
						>
							{type}
						</button>
					))}
				</div>
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					{t("startDateLabel")}
				</label>
				<div className="flex flex-wrap gap-2">
					{[
						"Now",
						"One month",
						"Three months",
						"Six months",
						"One year",
						"Other",
					].map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => setSingleValue("projectStartDate", type)}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
								watch("projectStartDate") === type
									? "bg-primary text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
							}`}
						>
							{type}
						</button>
					))}
				</div>
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					{t("teamLabel")}
				</label>
				<div className="flex flex-wrap gap-2">
					{["Employee", "Contractor", "Both"].map((type) => (
						<button
							key={type}
							type="button"
							onClick={() => setSingleValue("projectTeam", type)}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
								watch("projectTeam") === type
									? "bg-primary text-white"
									: "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
							}`}
						>
							{type}
						</button>
					))}
				</div>
			</div>

			<div>
				<Input
					{...register("projectBudget")}
					placeholder={t("budgetPlaceholder")}
					type="number"
					className={errors.projectBudget ? "border-red-500" : ""}
				/>
				{errors.projectBudget && (
					<p className="text-red-500 text-sm mt-1">
						{errors.projectBudget.message}
					</p>
				)}
			</div>

			<div>
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
					{t("detailsLabel")}
				</label>
				<textarea
					{...register("projectDetails")}
					placeholder={t("detailsPlaceholder")}
					className="flex min-h-[100px] w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary dark:border-gray-700 dark:text-white"
				/>
			</div>

			<FileUpload files={files} onFilesChange={setFiles} />

			{recaptchaError && (
				<p className="text-red-500 text-sm mt-1">{recaptchaError}</p>
			)}

			<Button
				type="submit"
				size="xl"
				disabled={status === "submitting" || !isValid || files.length === 0}
				loading={status === "submitting"}
				className="w-full"
			>
				{status === "submitting" ? t("sending") : t("button")}
			</Button>

			{status === "success" && (
				<p className="text-green-600 text-center">{t("success")}</p>
			)}
			{status === "error" && (
				<p className="text-red-600 text-center">{t("error")}</p>
			)}
		</form>
	);
}

function CollaborationFormWithRecaptcha() {
	const { executeRecaptcha } = useReCaptcha();
	const getToken = () => executeRecaptcha("collaboration_submit");
	return <CollaborationFormBase getToken={getToken} />;
}

export default function CollaborationForm() {
	if (!recaptchaSiteKey) return <CollaborationFormBase getToken={null} />;
	return <CollaborationFormWithRecaptcha />;
}
